import React, { useRef, useState } from "react";
import { UploadCloud, X, Loader2, Image as ImageIcon } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ImageUploader({ value = [], onChange, max = 10 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [drag, setDrag] = useState(false);

  const upload = async (files) => {
    const arr = Array.from(files).slice(0, max - value.length);
    if (!arr.length) return;
    setUploading(arr.length);
    const out = [...value];
    for (const f of arr) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} n'est pas une image`); continue; }
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} dépasse 8 Mo`); continue; }
      try {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
        out.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
        onChange([...out]);
      } catch (e) {
        toast.error("Échec de l'envoi: " + (e.response?.data?.detail || e.message));
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    upload(e.dataTransfer.files);
  };
  const remove = (i) => onChange(value.filter((_, j) => j !== i));

  return (
    <div data-testid="image-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer border-2 border-dashed rounded-2xl p-8 text-center transition ${drag ? "border-[#1B8F5A] bg-[#1B8F5A]/5" : "border-slate-300 hover:border-[#1B8F5A] hover:bg-slate-50"}`}
        data-testid="upload-dropzone"
      >
        <input ref={inputRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => upload(e.target.files)} data-testid="upload-input" />
        {uploading > 0 ? (
          <div className="flex flex-col items-center gap-2 text-slate-600">
            <Loader2 size={28} className="animate-spin text-[#1B8F5A]" />
            <span className="text-sm">Envoi… {uploading} image{uploading > 1 ? "s" : ""}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-slate-600">
            <UploadCloud size={32} className="text-[#1B8F5A]" />
            <div className="font-semibold text-[#111827]">Glissez vos photos ici</div>
            <div className="text-sm text-slate-500">ou cliquez pour parcourir · JPG/PNG/WebP · max 8 Mo</div>
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-3" data-testid="upload-preview">
          {value.map((url, i) => (
            <div key={i} className="relative aspect-[4/3] rounded-xl overflow-hidden border border-slate-200 group">
              <img src={url} alt="" className="w-full h-full object-cover" />
              {i === 0 && <span className="absolute top-1.5 left-1.5 text-[10px] bg-[#1B8F5A] text-white px-1.5 py-0.5 rounded font-bold uppercase">Principal</span>}
              <button type="button" onClick={() => remove(i)} className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition" data-testid={`remove-image-${i}`}>
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
